/**
 * F-131 — the roadmap's reads and writes against hermes.
 *
 *   points   GET  functions/v1/roadmap-points            (564: member read, service role + whitelist)
 *   result   RPC  get_roadmap_result(org)                 (579: the ONE computed result; KPI rows
 *                                                          withheld without finance access)
 *   access   RPC  roadmap_can_read(org), roadmap_can_edit(org)
 *   items    TABLE roadmap_items (SELECT only)            writes go through the RPCs below
 *   writes   RPC  roadmap_seed_items, roadmap_apply_change_set, roadmap_reset_order
 *   refresh  POST functions/v1/roadmap-maturity?action=recompute {organizationId, sessionId?}
 *                 (owner/admin token): derive, compute, store; with a session, freeze its snapshot
 *
 * Every RPC raises 42501 for anyone the access matrix refuses, and the points function answers
 * 403 to a reader on no paid plan; both are surfaced as RoadmapForbiddenError so the page shows
 * the gate, never an empty roadmap and never a retried refusal.
 */
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { supabase } from '@/lib/supabase';
import { isRoadmapEnabled } from '@/lib/feature-flags';
import type { AssetState, ConstraintPriority, PillarCode } from '@/lib/prime-score';
import type { SuggestedMoveEntry } from '@/lib/roadmap/suggested-moves';
import type { ChangeOp, RoadmapItem } from '@/lib/roadmap/types';
import type { Metric } from '@/types/tracking-view';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ROADMAP_POINTS_ENDPOINT = `${SUPABASE_URL}/functions/v1/roadmap-points`;
const ROADMAP_MATURITY_ENDPOINT = `${SUPABASE_URL}/functions/v1/roadmap-maturity`;

export class RoadmapForbiddenError extends Error {
  constructor(message = 'roadmap: not permitted') {
    super(message);
    this.name = 'RoadmapForbiddenError';
  }
}

function isForbidden(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === '42501' || /not permitted|permission denied/i.test(error.message ?? '');
}

async function getToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

// ── Points (content) ───────────────────────────────────────────────────────

export interface RoadmapRubricLine {
  level: number;
  label: string;
  description: string;
}
export interface RoadmapPointLesson {
  lesson_id: string;
  strength: 'teaches' | 'related' | string;
  source: string;
  lesson: { id: string; name: string; order: number | null; course: { id: string; name: string } | null } | null;
}
export interface RoadmapPointAsset {
  evidence_asset_id: string;
  strength: 'is_the_point' | 'related' | string;
  source: string;
  asset: { id: string; name: string; asset_type?: string | null } | null;
}
/** One of a question's answers, in display order (the five-answer scale: 1 Not in place … 5 Optimised). */
export interface RoadmapAnswerOption {
  id: string;
  display_order: number;
  label: string;
  /**
   * What answering this option scores, 0–100, as the server holds it. Optional because a result
   * stored before this build has options without it; fall back to position only then.
   */
  score?: number | null;
}
export interface RoadmapPointQuestion {
  question_code: string;
  source: string;
  is_primary: boolean;
  question: {
    id: string;
    code: string;
    question_text: string;
    type: string;
    version: number;
    /** Empty for a question with no options of its own (yes/no, 1–10, a number). */
    options?: RoadmapAnswerOption[];
  } | null;
}
export interface RoadmapPointPayload {
  id: string;
  roadmap_code: string | null;
  name: string;
  description: string | null;
  pillar: string | null;
  roadmap_order: number | null;
  is_roadmap_point: boolean;
  prerequisite: { id: string; roadmap_code: string | null; name: string } | null;
  rubric?: RoadmapRubricLine[];
  lessons?: RoadmapPointLesson[];
  assets?: RoadmapPointAsset[];
  questions?: RoadmapPointQuestion[];
}

export async function fetchRoadmapPoints(): Promise<RoadmapPointPayload[]> {
  const token = await getToken();
  const res = await fetch(`${ROADMAP_POINTS_ENDPOINT}?include=rubric,lessons,assets,questions`, {
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const message = (err as { error?: string }).error;
    // 403 is the function's paid gate answering (`not_entitled`), not an outage. Throw the
    // refusal the rest of the roadmap throws, so the hook does not retry it and the page
    // reads it as a gate.
    if (res.status === 403) throw new RoadmapForbiddenError(message || undefined);
    throw new Error(message ?? 'Could not load the roadmap points');
  }
  const body = (await res.json()) as { points: RoadmapPointPayload[] };
  return body.points ?? [];
}

// ── Access and state ───────────────────────────────────────────────────────

export interface RoadmapAccess {
  canRead: boolean;
  canEdit: boolean;
}

export async function fetchRoadmapAccess(organizationId: string): Promise<RoadmapAccess> {
  const [read, edit] = await Promise.all([
    supabase.rpc('roadmap_can_read', { p_org: organizationId }),
    supabase.rpc('roadmap_can_edit', { p_org: organizationId }),
  ]);
  if (read.error) throw new Error(read.error.message);
  if (edit.error) throw new Error(edit.error.message);
  return { canRead: read.data === true, canEdit: edit.data === true };
}

// ── The computed result (migration 579; supabase/functions/_shared/prometheus/roadmap-score.ts) ──

export type ResultsState = 'not_connected' | 'no_figures' | 'connected';

/** A finance KPI row as the server stored it: the cockpit's metric fields plus the engine's view. */
export interface ResultKpi {
  id: string;
  label: string;
  value: number | null;
  unit: Metric['unit'];
  /** The cockpit's own light (neutral when it has none). */
  status: Metric['status'];
  thresholds?: { red: number; amber: number; green: number };
  target?: { min?: number; max?: number };
  source: string;
  dataQuality: Metric['dataQuality'];
  reconciled?: boolean | null;
  contamination?: { severity: string; note: string; fixAccountId?: string | null };
  pillar: PillarCode;
  /** The light when the figure counts (ok data quality, not approximate), else null. */
  rag: 'green' | 'amber' | 'red' | null;
  counted: boolean;
  credit: number;
  /** The catalogue code (KPI_FIN_001…). Absent on a result stored before migration 580. */
  code?: string;
  /** The platform that measures it (`xero`, `wunderbuild`, `clickup`); null when none is named. */
  platform?: string | null;
  frequency?: string | null;
  /** The workbook's target wording, for a row with no numeric target. */
  targetText?: string | null;
  /** A live platform measures this row; the server withholds measured rows from a viewer without finance access. */
  measured?: boolean;
  /**
   * Prime points this row would add by turning green. Absent on a result stored before hermes
   * started writing it; treat absent as 0 (the row is not worth naming as a lever).
   */
  pointsToGreen?: number;
  /** The last stored periods, for the record. */
  history?: Array<{ period: string; value: number | null }>;
}

/** A platform that measures KPIs (roadmap_platforms): Xero live; Wunderbuild, ClickUp planned. */
export interface ResultPlatform {
  code: string;
  name: string;
  connectPath: string | null;
  status: 'live' | 'planned';
  pillars: PillarCode[];
}

export interface ResultPoint {
  level: number | null;
  built: boolean;
  confirmed: boolean;
  state: AssetState;
  prerequisiteMet: boolean;
  /** Level 3 or better on evidence alone, awaiting the owner's claim. */
  unconfirmed: boolean;
  /** Satisfied evidence as "kind:ref" (lesson_teaches:<id>, lesson_related:<id>, asset_is_the_point:<id>, asset_related:<id>, question:<code>). */
  done: string[];
  /**
   * How the point earns its levels: `doing` — it has lessons or assets, so 1 to 3 come from
   * doing them and its question completes 4 and 5 once Documented; `question` — nothing to
   * do, the question sets every level.
   */
  path: 'doing' | 'question';
  /** The question may be answered now: always on a question point, from Documented on a doing point. */
  questionOpen: boolean;
  /** The latest answer to the point's primary question (0–100 and when), or null. */
  answer: { score: number; at: string | null } | null;
  /** How many OTHER points share this point's primary question: one answer moves them all. */
  sharedWith: number;
  /** The KPIs that measure this point, the primary first. Absent on a result stored before migration 580. */
  kpis?: Array<{ code: string; primary: boolean }>;
  /**
   * Prime points this point would add by reaching level 3 (In place). Absent on a result stored
   * before hermes started writing it; treat absent as 0.
   */
  pointsToInPlace?: number;
  /**
   * This point's steps the organisation's membership does not include (hermes migration 682).
   * They do not count toward Documented. `ref` is the step id ("lesson:<id>" / "asset:<id>"),
   * `tier` the level that would open it, `reason` whether an upgrade would. Absent = none.
   */
  locked?: Array<{ ref: string; tier: string | null; reason: 'tier' | 'unavailable' }>;
}

export interface ResultPillar {
  pillar: PillarCode;
  label: string;
  pillarScore: number;
  assetHealthPoints: number;
  assetHealthMax: number;
  inPlaceCount: number;
  totalCount: number;
  kpiHealthPoints: number;
  kpiHealthMax: number;
  kpiCountedCount: number;
  kpiTotalCount: number;
  redKpis: number;
}

export interface ResultConstraint {
  pillar: PillarCode;
  assetId: string;
  priority: ConstraintPriority;
  reason: string;
  /**
   * What the builder has already said about this move, or null while it is open. Absent on a
   * result stored before 22 Sep 2026 and on a replay. A raw string: read it through
   * `moveResponse` (lib/roadmap/suggested-moves), never as a known state.
   */
  response?: string | null;
}

export interface RoadmapResult {
  version: string;
  /** The one "State as of" the tab shows. */
  computedAt: string;
  score: {
    assetHealthPoints: number;
    assetHealthMax: number;
    kpiHealthPoints: number;
    kpiHealthMax: number;
    bpsTotal: number;
    bpsMax: number;
    lifecycleStage: string;
    lifecycleLabel: string;
  };
  pillars: ResultPillar[];
  points: Record<string, ResultPoint>;
  constraint: ResultConstraint | null;
  /**
   * The coaching move, then its held runner-ups, one per pillar (F-131 alignment, D1). On a
   * result stored before the alignment: every point below level 3, ranked.
   */
  suggestedOrder: string[];
  /**
   * The move behind each id in `suggestedOrder`, same order. Absent on an older result.
   * `points` is the Prime points the move is worth when its point reaches level 3; absent on a
   * result stored before hermes started writing it, and treated as 0.
   *
   * `role` and `response` (22 Sep 2026) say which entry is the coaching move, which is its real
   * successor and which are other pillars' starting points, and what the builder already said
   * about the head. Both optional, both raw strings — see `lib/roadmap/suggested-moves`.
   */
  suggestedMoves?: SuggestedMoveEntry[];
  /** Why no constraint is named, when none is (no move yet, or the diagnostic is not complete). */
  constraintNote?: string | null;
  results: {
    state: ResultsState;
    source: 'Xero';
    /** When the figures shown were last pulled from Xero. */
    fetchedAt: string | null;
    /** The reporting period the figures are true as at ('2026-08'); absent before migration 580. */
    period?: string | null;
    /** The catalogue's platforms, so each pillar's Results half can signpost its own. */
    platforms?: ResultPlatform[];
    /** Empty when the reader has no finance access (the RPC withholds them). */
    kpis: ResultKpi[];
    /** Set by the RPC: false when the rows were withheld. */
    viewerHasFinanceAccess?: boolean;
  };
}

/** The stored result, or null when nothing has been computed for the org yet. */
export async function fetchRoadmapResult(organizationId: string): Promise<RoadmapResult | null> {
  const { data, error } = await supabase.rpc('get_roadmap_result', { p_org: organizationId });
  if (error) {
    if (isForbidden(error)) throw new RoadmapForbiddenError(error.message);
    throw new Error(error.message);
  }
  return (data as RoadmapResult | null) ?? null;
}

// ── Items ──────────────────────────────────────────────────────────────────

interface ItemRow {
  id: string;
  organization_id: string;
  asset_id: string | null;
  custom_title: string | null;
  custom_pillar: string | null;
  position: number | string;
  status: string;
  source: string;
  note: string | null;
  completed_at: string | null;
}

export function toRoadmapItem(r: ItemRow): RoadmapItem {
  return {
    id: r.id,
    organizationId: r.organization_id,
    assetId: r.asset_id,
    customTitle: r.custom_title,
    customPillar: (r.custom_pillar as PillarCode | null) ?? null,
    position: Number(r.position),
    aiPosition: null,
    status: r.status as RoadmapItem['status'],
    source: r.source as RoadmapItem['source'],
    note: r.note,
    completedAt: r.completed_at,
  };
}

export async function fetchRoadmapItems(organizationId: string): Promise<RoadmapItem[]> {
  const { data, error } = await supabase
    .from('roadmap_items')
    .select('*')
    .eq('organization_id', organizationId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as ItemRow[]).map(toRoadmapItem);
}

// ── Writes (RPCs; the server re-checks org, role and paid on every call) ───

function rpcError(error: { code?: string; message: string }): Error {
  return isForbidden(error) ? new RoadmapForbiddenError(error.message) : new Error(error.message);
}


export interface ApplyChangeSetInput {
  rationale: string;
  proposedBy: 'alfie' | 'member';
  ops: ChangeOp[];
  status: 'approved' | 'discarded';
}

export interface ApplyChangeSetResult {
  changeSetId: string;
  items: RoadmapItem[];
}



export type SnapshotTrigger = 'diagnostic' | 'recompute' | 'nightly' | 'weekly' | 'backfill';

export interface RoadmapSnapshot {
  id: string;
  organization_id: string;
  session_id: string | null;
  trigger: SnapshotTrigger;
  captured_at: string;
  as_of: string;
  asset_health_points: number;
  kpi_health_points: number | null;
  bps_total: number;
  lifecycle_stage: string;
  constraint_pillar: string | null;
  pillar_points: Record<string, { asset_health: number; asset_max: number; kpi_health: number; kpi_max: number; in_place: number; total: number; red_kpis: number }>;
  /** Points at level 2 or above at capture (older rows carry every point); a missing point reads 1. */
  maturity: Record<string, number | null>;
  formula_version: string;
}

function toSnapshot(raw: Record<string, unknown>): RoadmapSnapshot {
  const num = (v: unknown): number => (v === null || v === undefined ? 0 : Number(v));
  return {
    ...(raw as unknown as RoadmapSnapshot),
    asset_health_points: num(raw.asset_health_points),
    kpi_health_points: raw.kpi_health_points === null || raw.kpi_health_points === undefined
      ? null
      : Number(raw.kpi_health_points),
    bps_total: num(raw.bps_total),
  };
}

export async function fetchRoadmapSnapshots(organizationId: string): Promise<RoadmapSnapshot[]> {
  const { data, error } = await supabase
    .from('roadmap_score_snapshots')
    .select('*')
    .eq('organization_id', organizationId)
    .order('captured_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Record<string, unknown>[]).map(toSnapshot);
}

// ── Recompute (the coaching function; owner/admin token) ───────────────────

export interface RecomputeResponse {
  organizationId: string;
  result: RoadmapResult;
  /** False when a newer result was already stored (an overlapping run finished first). */
  stored: boolean;
  /** The frozen snapshot for `sessionId`, when one was given. */
  snapshotId: string | null;
  points: number;
  built: number;
  confirmed: number;
  durationMs: number;
}

/**
 * Derive, compute and store the org's result; with `sessionId` (a diagnostic run just completed)
 * the same call freezes that run's snapshot. Used by Recalculate now, the page-open refresh,
 * the claim refresh and the diagnostic completion.
 */
// ── The question step (roadmap-maturity?action=answer) ──────────────────────

/** An answer in the question's own shape: 1–5 or 1–10 as a number, yes/no as a boolean, a number. */
export interface RoadmapAnswer {
  answerNumeric?: number;
  answerBoolean?: boolean;
  answerValue?: string;
}

/** How much of the move in an answer's body can be trusted (hermes `CurrentMoveStatus`). */
export type AnswerMoveStatus = 'set' | 'none' | 'unavailable' | 'pending';

/**
 * `roadmap-maturity?action=answer`, in both of its shapes (22 Sep 2026).
 *
 * An answer on any point EXCEPT today's open move is the roadmap's own path: the server writes
 * it, recalculates, re-picks the move and stores the result before it replies, so `result` and
 * `point` are set and `moveAnswered` is false.
 *
 * An answer on the point of today's OPEN move is an answer to the move. The server commits it
 * through the coaching card's own path and replies at the commit with the card's envelope:
 * `moveAnswered: true`, `currentMoveStatus: 'pending'`, `roadmapRefreshDeferred: true`, and NO
 * `result` or `point` — the recalculation, the recompute and the result store run after the
 * reply. The move is now resting, so the page re-reads `get_current_move` (which locks the
 * question) and waits for the stored result to move on before re-reading the roadmap.
 */
export interface AnswerResponse {
  organizationId: string;
  assetId: string;
  question: { id: string; code: string; type: string };
  /** The 0–100 the answer scored, or null for "I don't know". */
  score: number | null;
  /**
   * Where the answer landed: written, written_recalc_failed, or — only when it answered the
   * move, where the card's pillar cap applies — skipped_cap / skipped_no_session.
   */
  written: string;
  point: ResultPoint | null;
  result: RoadmapResult | null;
  stored: boolean;
  /** True when the answer was recorded as the response to today's move, which is now resting. */
  moveAnswered?: boolean;
  /** `pending` on an answer to the move: nobody has looked yet, so refetch, never render empty. */
  currentMoveStatus?: AnswerMoveStatus;
  /** The server stores the roadmap result itself after this answer: invalidate, never recompute. */
  roadmapRefreshDeferred?: true;
  /** The move that was answered and the response recorded on it (`done` or `answered`). */
  moveId?: string;
  response?: 'done' | 'answered';
}

/** The server refused the answer; `code` is `not_documented` when the point's lessons come first. */
export class RoadmapAnswerError extends Error {
  constructor(message: string, public readonly code: string | null, public readonly status: number) {
    super(message);
    this.name = 'RoadmapAnswerError';
  }
}

/**
 * Answer a point's primary diagnostic question from the roadmap (owner/admin). The server writes
 * it onto the organisation's current diagnostic session, recalculates the 0–100, recomputes and
 * stores the roadmap result, and returns that result with the point's new state — unless the
 * point is today's open move, in which case the answer is the move's response and the result
 * follows later (see `AnswerResponse`). A request that loses that race to the card is a 409 whose
 * message says the move has already been answered.
 */
export async function answerRoadmapQuestion(
  organizationId: string,
  assetId: string,
  answer: RoadmapAnswer,
): Promise<AnswerResponse> {
  const token = await getToken();
  const res = await fetch(`${ROADMAP_MATURITY_ENDPOINT}?action=answer`, {
    method: 'POST',
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ organizationId, assetId, answer }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
      error?: string;
      details?: { code?: string };
    };
    throw new RoadmapAnswerError(err.error ?? 'Could not save your answer', err.details?.code ?? null, res.status);
  }
  return (await res.json()) as AnswerResponse;
}

export async function recomputeRoadmapState(
  organizationId: string,
  sessionId: string | null = null,
): Promise<RecomputeResponse | null> {
  // A dark feature writes nothing: the flag gates every background recompute here, so
  // no call site (after a diagnostic, on page load, after a claim) needs its own check.
  if (!isRoadmapEnabled()) return null;
  const token = await getToken();
  const res = await fetch(`${ROADMAP_MATURITY_ENDPOINT}?action=recompute`, {
    method: 'POST',
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(sessionId ? { organizationId, sessionId } : { organizationId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Could not recalculate the roadmap');
  }
  return (await res.json()) as RecomputeResponse;
}
